import { Children, useEffect, useState, type ReactNode } from 'react'

// ================================================================= page ====

export function Page({
  title,
  subtitle,
  actions,
  children,
}: {
  title: string
  subtitle?: string
  actions?: ReactNode
  children: ReactNode
}) {
  return (
    <div className="page">
      <header className="page-head">
        <div>
          <h1>{title}</h1>
          {subtitle && <p className="muted">{subtitle}</p>}
        </div>
        {actions && <div className="page-actions">{actions}</div>}
      </header>
      {children}
    </div>
  )
}

// ================================================================ tiles ====

export function StatTile({
  label,
  value,
  sub,
  tone,
}: {
  label: string
  value: string | number
  sub?: string
  tone?: 'ok' | 'warn' | 'danger' | 'live'
}) {
  return (
    <div className={`tile${tone ? ` tone-${tone}` : ''}`}>
      <div className="tile-label">{label}</div>
      <div className="tile-value">{value}</div>
      {sub && <div className="tile-sub">{sub}</div>}
    </div>
  )
}

/**
 * A row of stat tiles that always breaks into even rows.
 *
 * `auto-fit`/`minmax` was the obvious choice and is what produced the fault
 * this replaces: at some widths six tiles fit five across and left the sixth
 * stranded alone on the next line. The column count is derived from how many
 * tiles there actually are, so six become 6/3/2/1 and four become 4/2/1 — never
 * a 5+1 or a 3+1.
 */
export function TileRow({ children }: { children: ReactNode }) {
  const count = Children.count(children)
  return (
    <div className="tile-row" data-tiles={count}>
      {children}
    </div>
  )
}

// ================================================================ panel ====

export function Panel({
  title,
  actions,
  children,
  className,
}: {
  title?: string
  actions?: ReactNode
  children: ReactNode
  className?: string
}) {
  return (
    <section className={`panel${className ? ` ${className}` : ''}`}>
      {(title || actions) && (
        <div className="panel-head">
          {title && <h2>{title}</h2>}
          {actions}
        </div>
      )}
      <div className="panel-body">{children}</div>
    </section>
  )
}

// ================================================================ table ====

export function Table({ head, children }: { head: string[]; children: ReactNode }) {
  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            {head.map((h) => (
              <th key={h}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  )
}

export function Empty({ message, hint }: { message: string; hint?: string }) {
  return (
    <div className="empty">
      <p>{message}</p>
      {hint && <p className="muted small">{hint}</p>}
    </div>
  )
}

// ================================================================ modal ====

export function Modal({
  title,
  onClose,
  children,
  wide,
}: {
  title: string
  onClose: () => void
  children: ReactNode
  wide?: boolean
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose()
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className={`modal${wide ? ' modal-wide' : ''}`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="modal-head">
          <h2>{title}</h2>
          <button className="ghost" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>
        <div className="modal-body">{children}</div>
      </div>
    </div>
  )
}

// ================================================================ forms ====

export function Field({
  label,
  hint,
  children,
}: {
  label: string
  hint?: string
  children: ReactNode
}) {
  return (
    <label className="field">
      <span className="field-label">{label}</span>
      {children}
      {hint && <span className="field-hint">{hint}</span>}
    </label>
  )
}

export function FormGrid({ children }: { children: ReactNode }) {
  return <div className="form-grid">{children}</div>
}

// ============================================================== badges ====

export function Badge({
  children,
  tone,
}: {
  children: ReactNode
  tone?: 'ok' | 'warn' | 'danger' | 'live' | 'muted'
}) {
  return <span className={`badge${tone ? ` badge-${tone}` : ''}`}>{children}</span>
}

export function StateBadge({ state }: { state: string }) {
  const tone =
    state === 'ready'
      ? 'ok'
      : state === 'talking'
        ? 'live'
        : state === 'paused'
          ? 'muted'
          : state === 'wrap'
            ? 'warn'
            : 'warn'
  return <Badge tone={tone as never}>{state.replace('_', ' ')}</Badge>
}

// ============================================================== notices ====

export function Notice({
  kind,
  children,
  onDismiss,
}: {
  kind: 'error' | 'info' | 'success' | 'warn'
  children: ReactNode
  onDismiss?: () => void
}) {
  return (
    <div className={`notice notice-${kind}`}>
      <span>{children}</span>
      {onDismiss && (
        <button className="ghost" onClick={onDismiss} aria-label="Dismiss">
          ×
        </button>
      )}
    </div>
  )
}

// =============================================================== charts ====

/**
 * A bar chart drawn as SVG.
 *
 * No chart library. This is one chart type with one behaviour, and a
 * dependency for it would outweigh the code by two orders of magnitude.
 */
export function BarChart({
  data,
  height = 180,
}: {
  data: { label: string; values: { value: number; color: string; name: string }[] }[]
  height?: number
}) {
  if (data.length === 0) return <Empty message="No activity in this period." />

  const max = Math.max(1, ...data.flatMap((d) => d.values.map((v) => v.value)))
  const barW = 100 / data.length

  return (
    <div className="chart">
      <svg viewBox={`0 0 100 ${height}`} preserveAspectRatio="none" className="chart-svg">
        {[0.25, 0.5, 0.75, 1].map((f) => (
          <line
            key={f}
            x1="0"
            x2="100"
            y1={height - f * height * 0.88}
            y2={height - f * height * 0.88}
            className="chart-grid"
            vectorEffect="non-scaling-stroke"
          />
        ))}
        {data.map((d, i) => {
          const group = d.values.length
          const w = (barW * 0.7) / group
          return d.values.map((v, j) => {
            const h = (v.value / max) * height * 0.88
            return (
              <rect
                key={`${i}-${j}`}
                x={i * barW + barW * 0.15 + j * w}
                y={height - h}
                width={w}
                height={h}
                fill={v.color}
              >
                <title>{`${d.label} — ${v.name}: ${v.value}`}</title>
              </rect>
            )
          })
        })}
      </svg>
      <div className="chart-axis">
        {data.map((d, i) => (
          <span key={i}>{d.label}</span>
        ))}
      </div>
      <div className="chart-legend">
        {data[0]?.values.map((v) => (
          <span key={v.name}>
            <i style={{ background: v.color }} />
            {v.name}
          </span>
        ))}
      </div>
    </div>
  )
}

// ================================================================ hooks ====

/** Poll an async source on an interval, with the first fetch immediate. */
export function usePoll<T>(fn: () => Promise<T>, ms: number, deps: unknown[] = []) {
  const [data, setData] = useState<T | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    const run = async () => {
      try {
        const d = await fn()
        if (!cancelled) {
          setData(d)
          setError(null)
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Request failed')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    run()
    if (ms <= 0) return () => { cancelled = true }
    const t = setInterval(run, ms)
    return () => {
      cancelled = true
      clearInterval(t)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps)

  return { data, error, loading, reload: () => setLoading(true) }
}

// ============================================================ formatting ====

export function fmtPhone(e164: string): string {
  const m = /^\+1(\d{3})(\d{3})(\d{4})$/.exec(e164)
  return m ? `(${m[1]}) ${m[2]}-${m[3]}` : e164
}

export function fmtDuration(sec: number | null | undefined): string {
  if (!sec || sec < 0) return '—'
  const h = Math.floor(sec / 3600)
  const m = Math.floor((sec % 3600) / 60)
  const s = Math.floor(sec % 60)
  return h > 0
    ? `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
    : `${m}:${String(s).padStart(2, '0')}`
}

export function fmtTime(iso: string): string {
  return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

export function fmtDateTime(iso: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  return `${d.toLocaleDateString([], { month: 'short', day: 'numeric' })} ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
}

export function fmtNumber(n: number): string {
  return n.toLocaleString()
}

/** The called party's local time, so nobody says "good morning" at 7pm. */
export function localTime(timezone: string | null): string | null {
  if (!timezone) return null
  try {
    return new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      hour: 'numeric',
      minute: '2-digit',
    }).format(new Date())
  } catch {
    return null
  }
}

export const CHART_COLORS = {
  attempts: '#3d4761',
  answered: '#35c1ff',
  connects: '#2ecc8f',
  contacts: '#ff2d78',
}
